/**
 * The Settings sections (HOF-627 settings refactor): one pane each at `/settings/<id>`, grouped in
 * the nav as Persönlich (the person's own account) and Betrieb (the farm they work in).
 *
 * Which ones a person sees follows their seat — `SettingsPage` computes the set; this module only
 * names them, in nav order.
 */
import type { LucideIcon } from 'lucide-react';
import { Building2, Mail, Network, SlidersHorizontal, User, UserCog, Users } from 'lucide-react';

export type SettingsSectionId = 'profile' | 'account' | 'farm' | 'parameters' | 'team' | 'email' | 'organisations';
export type SettingsGroupId = 'personal' | 'farm';

export interface SettingsSectionDef {
    id: SettingsSectionId;
    group: SettingsGroupId;
    labelKey: string;
    fallback: string;
    icon: LucideIcon;
}

export const SETTINGS_SECTIONS: readonly SettingsSectionDef[] = [
    { id: 'profile', group: 'personal', labelKey: 'settings.nav.profile', fallback: 'Profil', icon: User },
    { id: 'account', group: 'personal', labelKey: 'settings.nav.account', fallback: 'Konto', icon: UserCog },
    { id: 'farm', group: 'farm', labelKey: 'settings.nav.farm', fallback: 'Betrieb', icon: Building2 },
    // HOF-708: the farm's own calculation settings — every farm-editable setting has its editor here.
    {
        id: 'parameters',
        group: 'farm',
        labelKey: 'settings.nav.parameters',
        fallback: 'Betriebsparameter',
        icon: SlidersHorizontal,
    },
    { id: 'team', group: 'farm', labelKey: 'settings.nav.team', fallback: 'Team', icon: Users },
    { id: 'email', group: 'farm', labelKey: 'settings.nav.email', fallback: 'E-Mail-Einrichtung', icon: Mail },
    {
        id: 'organisations',
        group: 'farm',
        labelKey: 'settings.nav.organisations',
        fallback: 'Organisationen',
        icon: Network,
    },
];

export const SETTINGS_GROUPS: readonly { id: SettingsGroupId; labelKey: string; fallback: string }[] = [
    { id: 'personal', labelKey: 'settings.nav.personal', fallback: 'Persönlich' },
    { id: 'farm', labelKey: 'settings.nav.farmGroup', fallback: 'Betrieb' },
];

/** The pane `/settings` opens on (desktop) — always available. */
export const DEFAULT_SECTION: SettingsSectionId = 'profile';

export const settingsPath = (id: SettingsSectionId): string => `/settings/${id}`;
